import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { FilterEntitySourceId, RowForSource } from "building-types/buildingGraphFilter/entitySource";
import type { PropertyPath } from "building-types/buildingGraphFilter/propertyCatalog";
import type {
  DocBuildingGraphFilterGraph,
  DocBuildingGraphFilterNode,
  DocBuildingGraphPredicateFilterNode,
  DocBuildingGraphSubfilterValueSource,
} from "building-types/walls2/wallPlaneFilters";
import deepequal from "common/utils/deepequal";
import { getEntitySource } from "./entityFilterSourceImpl";
import type { EntityFilterSource, PropertyValue } from "./entityFilterSourceTypes";
import { traverseDocFilterGraph, type TraverseDocFilterGraphEvaluateNodeArgs } from "./validateDocFilterGraph";

export function executeDocBuildingGraphFilter<T extends FilterEntitySourceId>(
  targetType: T,
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  filterGraphId: string | undefined,
  building: BuildingGraphFilterBuilding,
  operationId: string,
): Map<string, RowForSource[T]> {
  const source = getEntitySource(targetType);
  return executeDocBuildingGraphFilterWithSource(source, filterGraphs, filterGraphId, building, operationId);
}

export function executeDocBuildingGraphFilterWithSource<T extends FilterEntitySourceId>(
  source: EntityFilterSource<T>,
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  filterGraphId: string | undefined,
  building: BuildingGraphFilterBuilding,
  operationId: string,
): Map<string, RowForSource[T]> {
  const universe = getAllEntitiesForType(source, building);
  if (!filterGraphId) {
    return universe;
  }
  return evaluateDocGraph(source, filterGraphs, filterGraphId, universe, building, operationId);
}

export function getAllEntitiesForType<T extends FilterEntitySourceId>(
  source: EntityFilterSource<T>,
  building: BuildingGraphFilterBuilding,
): Map<string, RowForSource[T]> {
  const universe = new Map<string, RowForSource[T]>();
  for (const row of source.iterateRows(building)) {
    universe.set(source.identity(row), row);
  }
  return universe;
}

function evaluateDocGraph<T extends FilterEntitySourceId>(
  source: EntityFilterSource<T>,
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  graphId: string,
  universe: Map<string, RowForSource[T]>,
  building: BuildingGraphFilterBuilding,
  operationId: string,
): Map<string, RowForSource[T]> {
  const result = traverseDocFilterGraph<Map<string, RowForSource[T]>>(
    filterGraphs,
    graphId,
    source.sourceId,
    operationId,
    (args) => evaluateDocNodeFromTraversal(source, filterGraphs, args, universe, building, operationId),
  );
  if (!result) {
    throw new Error(`WallPlaneOperation "${operationId}" produced no evaluation result`);
  }
  return result;
}

function evaluateDocNodeFromTraversal<T extends FilterEntitySourceId>(
  source: EntityFilterSource<T>,
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  args: TraverseDocFilterGraphEvaluateNodeArgs<Map<string, RowForSource[T]>>,
  universe: Map<string, RowForSource[T]>,
  building: BuildingGraphFilterBuilding,
  operationId: string,
): Map<string, RowForSource[T]> {
  const { graphId, node, childResult, childResults } = args;
  switch (node.filterType) {
    case "AND":
      return evaluateDocAnd(
        filterGraphs,
        graphId,
        node.childrenNodeIds,
        requireChildResults(node, childResults),
        universe,
        operationId,
      );
    case "OR":
      return evaluateDocOr(
        filterGraphs,
        graphId,
        node.childrenNodeIds,
        requireChildResults(node, childResults),
        operationId,
      );
    case "NOT":
      return requireChildResult(node, childResult);
    case "PREDICATE":
      return evaluateDocPredicate(source, filterGraphs, node, universe, building, operationId);
    default:
      return assertUnreachable(node);
  }
}

function requireChildResults<T>(
  node: Extract<DocBuildingGraphFilterNode, { readonly filterType: "AND" | "OR" }>,
  childResults: readonly T[] | undefined,
): readonly T[] {
  if (!childResults) {
    throw new Error(`Missing child results for ${node.filterType} node`);
  }
  return childResults;
}

function requireChildResult<T>(
  node: Extract<DocBuildingGraphFilterNode, { readonly filterType: "NOT" }>,
  childResult: T | undefined,
): T {
  if (!childResult) {
    throw new Error(`Missing child result for ${node.filterType} node`);
  }
  return childResult;
}

function evaluateDocAnd<T extends FilterEntitySourceId>(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  graphId: string,
  childNodeIds: readonly string[],
  childResults: readonly Map<string, RowForSource[T]>[],
  universe: Map<string, RowForSource[T]>,
  operationId: string,
): Map<string, RowForSource[T]> {
  if (childNodeIds.length === 0) {
    return new Map(universe);
  }
  const positives: Map<string, RowForSource[T]>[] = [];
  const negatives: Map<string, RowForSource[T]>[] = [];
  for (const [index, childNodeId] of childNodeIds.entries()) {
    const childNode = filterGraphs[graphId]?.nodes[childNodeId];
    if (!childNode) {
      throw new Error(`WallPlaneOperation "${operationId}" references missing filter node "${childNodeId}"`);
    }
    const childResult = childResults[index];
    if (!childResult) {
      throw new Error(`WallPlaneOperation "${operationId}" missing evaluated child result for node "${childNodeId}"`);
    }
    if (childNode.filterType === "NOT") {
      negatives.push(childResult);
    } else {
      positives.push(childResult);
    }
  }
  if (positives.length === 0) {
    throw new Error("AND with only NOT children is not supported; at least one positive branch is required");
  }

  let acc = new Map(positives[0]);
  for (const positive of positives.slice(1)) {
    acc = intersect(acc, positive);
  }
  for (const negResult of negatives) {
    for (const key of negResult.keys()) {
      acc.delete(key);
    }
  }
  return acc;
}

function evaluateDocOr<T extends FilterEntitySourceId>(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  graphId: string,
  childNodeIds: readonly string[],
  childResults: readonly Map<string, RowForSource[T]>[],
  operationId: string,
): Map<string, RowForSource[T]> {
  const result = new Map<string, RowForSource[T]>();
  for (const [index, childNodeId] of childNodeIds.entries()) {
    const childNode = filterGraphs[graphId]?.nodes[childNodeId];
    if (!childNode) {
      throw new Error(`WallPlaneOperation "${operationId}" references missing filter node "${childNodeId}"`);
    }
    if (childNode.filterType === "NOT") {
      throw new Error("NOT may only appear as a child of AND; OR(NOT(...)) is not supported");
    }
    const childResult = childResults[index];
    if (!childResult) {
      throw new Error(`WallPlaneOperation "${operationId}" missing evaluated child result for node "${childNodeId}"`);
    }
    for (const [key, row] of childResult) {
      result.set(key, row);
    }
  }
  return result;
}

function evaluateDocPredicate<T extends FilterEntitySourceId>(
  source: EntityFilterSource<T>,
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  predicate: DocBuildingGraphPredicateFilterNode,
  universe: Map<string, RowForSource[T]>,
  building: BuildingGraphFilterBuilding,
  operationId: string,
): Map<string, RowForSource[T]> {
  const path = predicate.path as PropertyPath<T>;
  const result = new Map<string, RowForSource[T]>();
  const descriptor = source.descriptor(path);
  if (!descriptor) {
    throw new Error(
      `WallPlaneOperation "${operationId}" filter has invalid path "${path}" for source "${source.sourceId}"`,
    );
  }
  const rhsValues = resolveDocPredicateValues(source, filterGraphs, predicate, building, operationId);
  for (const [key, row] of universe) {
    const value = source.readProperty(row, path, building);
    if (matchPredicate(predicate, descriptor.kind, value, rhsValues)) {
      result.set(key, row);
    }
  }
  return result;
}

function resolveDocPredicateValues<T extends FilterEntitySourceId>(
  source: EntityFilterSource<T>,
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  predicate: DocBuildingGraphPredicateFilterNode,
  building: BuildingGraphFilterBuilding,
  operationId: string,
): readonly unknown[] | undefined {
  if (predicate.values !== undefined && predicate.valueSource !== undefined) {
    throw new Error(`Predicate at "${predicate.path}" cannot provide both values and valueSource`);
  }
  if (predicate.valueSource !== undefined) {
    return resolveDocValuesFromSubfilterSource(source, filterGraphs, predicate.valueSource, building, operationId);
  }
  if (predicate.values !== undefined) {
    return predicate.values;
  }
  return undefined;
}

function resolveDocValuesFromSubfilterSource<T extends FilterEntitySourceId>(
  _source: EntityFilterSource<T>,
  filterGraphs: Record<string, DocBuildingGraphFilterGraph>,
  valueSource: DocBuildingGraphSubfilterValueSource,
  building: BuildingGraphFilterBuilding,
  operationId: string,
): readonly unknown[] {
  const source = getEntitySource(valueSource.sourceType);
  const selectPaths = valueSource.selectPaths as readonly PropertyPath<typeof valueSource.sourceType>[];
  const matches = valueSource.filterGraphId
    ? evaluateDocGraph(
        source,
        filterGraphs,
        valueSource.filterGraphId,
        getAllEntitiesForType(source, building),
        building,
        operationId,
      )
    : getAllEntitiesForType(source, building);

  const values = new Set<unknown>();
  for (const row of matches.values()) {
    for (const selectPath of selectPaths) {
      const selectedValue = source.readProperty(row, selectPath, building);
      if (selectedValue.kind === "SCALAR") {
        if (selectedValue.value !== undefined && selectedValue.value !== null) {
          values.add(selectedValue.value);
        }
      } else {
        for (const value of selectedValue.values) {
          values.add(value);
        }
      }
    }
  }
  return [...values];
}

function matchesFilterLiteral(propertyValue: unknown, filterLiteral: unknown): boolean {
  if (filterLiteral === null || filterLiteral === undefined) {
    return propertyValue === null || propertyValue === undefined;
  }
  return deepequal(propertyValue, filterLiteral);
}

function matchPredicate(
  predicate: Pick<DocBuildingGraphPredicateFilterNode, "op" | "path">,
  kind: "SCALAR" | "SET",
  value: PropertyValue,
  rhsValues: readonly unknown[] | undefined,
): boolean {
  switch (predicate.op) {
    case "EQ":
    case "NEQ": {
      if (kind !== "SCALAR") {
        throw new Error(`${predicate.op} requires SCALAR property at "${predicate.path}"`);
      }
      if (value.kind !== "SCALAR") {
        throw new Error(`Descriptor/value kind mismatch at "${predicate.path}"`);
      }
      const rhsValue = requireSingleValue(predicate, rhsValues);
      const eq = matchesFilterLiteral(value.value, rhsValue);
      return predicate.op === "EQ" ? eq : !eq;
    }
    case "IN": {
      if (kind !== "SCALAR") {
        throw new Error(`IN requires SCALAR property at "${predicate.path}"`);
      }
      if (value.kind !== "SCALAR") {
        throw new Error(`Descriptor/value kind mismatch at "${predicate.path}"`);
      }
      const values = requireValues(predicate, rhsValues);
      return values.some((rhsValue) => matchesFilterLiteral(value.value, rhsValue));
    }
    case "CONTAINS": {
      if (kind !== "SET") {
        throw new Error(`CONTAINS requires SET property at "${predicate.path}"`);
      }
      if (value.kind !== "SET") {
        throw new Error(`Descriptor/value kind mismatch at "${predicate.path}"`);
      }
      const rhsValue = requireSingleValue(predicate, rhsValues);
      return value.values.has(rhsValue);
    }
    case "INTERSECTS": {
      if (kind !== "SET") {
        throw new Error(`INTERSECTS requires SET property at "${predicate.path}"`);
      }
      if (value.kind !== "SET") {
        throw new Error(`Descriptor/value kind mismatch at "${predicate.path}"`);
      }
      const values = requireValues(predicate, rhsValues);
      return values.some((rhsValue) => value.values.has(rhsValue));
    }
    case "EXISTS": {
      if (rhsValues !== undefined) {
        throw new Error(`EXISTS does not accept values/valueSource at "${predicate.path}"`);
      }
      return value.kind === "SCALAR" ? value.value !== null && value.value !== undefined : value.values.size > 0;
    }
  }
}

function requireValues(
  predicate: Pick<DocBuildingGraphPredicateFilterNode, "op" | "path">,
  rhsValues: readonly unknown[] | undefined,
): readonly unknown[] {
  if (rhsValues === undefined) {
    throw new Error(`${predicate.op} requires values or valueSource at "${predicate.path}"`);
  }
  return rhsValues;
}

function requireSingleValue(
  predicate: Pick<DocBuildingGraphPredicateFilterNode, "op" | "path">,
  rhsValues: readonly unknown[] | undefined,
): unknown {
  const values = requireValues(predicate, rhsValues);
  if (values.length !== 1) {
    throw new Error(
      `${predicate.op} requires exactly one entry in "values" / valueSource result at "${predicate.path}" (got ${values.length})`,
    );
  }
  return values[0];
}

function assertUnreachable(value: never): never {
  throw new Error(`Unexpected value: ${JSON.stringify(value)}`);
}

function intersect<T extends FilterEntitySourceId>(
  a: Map<string, RowForSource[T]>,
  b: Map<string, RowForSource[T]>,
): Map<string, RowForSource[T]> {
  const out = new Map<string, RowForSource[T]>();
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  for (const [key, row] of small) {
    if (large.has(key)) {
      out.set(key, row);
    }
  }
  return out;
}
